/**
 * Cloud and local items (Free plan) — the app side of cards C2/C3 (plan in
 * Apiboo Documents › Changes › "Plan — Cloud and local items (Free plan)").
 *
 * A signed-in account's boards and documents live in one of two local scopes:
 *   - CLOUD  `personal:<userId>` — synced (web app + other devices)
 *   - DEVICE `device:<userId>`   — on this computer only; nothing is queued
 *     for the server (src-tauri/src/sync/item_location.rs)
 * Team items live in `team:<teamId>` and are always cloud.
 *
 * Moving keeps ids, so links keep working. "Move to cloud" is all or nothing:
 * the server stores every record of the item or none (atomic push), and a
 * refusal (e.g. the Free board limit) changes nothing anywhere.
 *
 * This file answers WHERE an item lives and where a new one goes. MOVING one is
 * `./itemMove`, kept apart on purpose (17.09.2026): this module is in the MCP
 * import graph through documentsStore/boardsStore, and the move commands must
 * not be. Keep this file invoke-free — `mcpAttackSurface.test.js` will say so.
 */
// From utils/isTauri, NOT utils/tauri: that file carries sendHttpRequest, and
// this module is in the MCP import graph.
import { isTauri } from '../utils/isTauri';
import { useAuthStore } from '../store/authStore';
import { notePlanLimitDeviceItem } from '../store/planLimitStore';
import { limitRows } from '../services/planUsage';
import { newItemsStayOnDevice } from '../plans/cloudAccess';

export const LOCATION = Object.freeze({ CLOUD: 'cloud', DEVICE: 'device', TEAM: 'team' });
export const DEVICE_SCOPE_PREFIX = 'device:';

/** `device:<userId>` for the signed-in desktop user, else null (web / signed out). */
export function deviceScopeId() {
  if (!isTauri()) return null;
  const { isAuthenticated, user } = useAuthStore.getState();
  return isAuthenticated && user?.id != null ? `${DEVICE_SCOPE_PREFIX}${user.id}` : null;
}

export function isDeviceScope(scopeKey) {
  return String(scopeKey || '').startsWith(DEVICE_SCOPE_PREFIX);
}

/** Where a record scope key lives, for badges and routing. */
export function locationOfScope(scopeKey) {
  const key = String(scopeKey || '');
  if (key.startsWith(DEVICE_SCOPE_PREFIX)) return LOCATION.DEVICE;
  if (key.startsWith('team:')) return LOCATION.TEAM;
  return LOCATION.CLOUD;
}

/**
 * Where a NEW board or document should be created (card C5).
 *
 * To the cloud while the plan still has room for it, and to this device once
 * it does not — never a refusal. The alternative, blocking creation at the
 * limit, means a Free user who reached three boards cannot make a fourth
 * anywhere, on a desktop app that works offline; this way the item is simply
 * theirs alone until they upgrade or move something out of the cloud.
 *
 * `kind` is 'boards' or 'documents' (the planUsage row keys). Anything this
 * cannot answer — no usage loaded yet, a paid plan, the web app, signed out —
 * routes to the cloud, which is the behaviour every non-Free account has.
 */
export function scopeForNewItem(kind) {
  const device = deviceScopeId();
  if (!device) return null; // web app or signed out: the caller's default scope
  // Plans v2 card 3.4: no `cloud_sync` (Free, Basic) → always this device;
  // Pro / Team → the Settings default, then the cloud while it has room.
  if (newItemsStayOnDevice()) return device;
  return cloudIsFull(kind) ? device : null;
}

/**
 * `cloudIsFull`, but against FRESH numbers.
 *
 * `user._usage` is refreshed on mount, after a create and after a refusal —
 * never after a delete. So a user who deleted a board to make room was still
 * told the cloud was full until they reloaded the page, and a count that had
 * drifted the other way let a doomed create through. This is only called at
 * the moment of a decision, so one request is cheap; if it fails, the last
 * known numbers answer, which is what `cloudIsFull` would have said anyway.
 */
export async function cloudIsFullNow(kind) {
  try {
    await useAuthStore.getState().fetchUsage?.();
  } catch {
    /* fall back to what we already had */
  }
  return cloudIsFull(kind);
}

/**
 * Has this plan run out of room in the cloud for that kind of item?
 *
 * On the desktop this only decides WHERE a new item goes. On the web it is a
 * hard stop: the web app holds nothing locally, so a board created past the
 * limit would be refused by the server and lost on the next reload. The user
 * is told instead — see components/ui/CloudFullModal.
 *
 * Anything unanswerable (usage not loaded, a plan with no limits) is "not
 * full": never block on a number we do not have.
 */
export function cloudIsFull(kind) {
  try {
    const row = limitRows(useAuthStore.getState().user?._usage).find((r) => r.key === kind);
    return Boolean(row && row.state === 'full');
  } catch {
    return false;
  }
}

/**
 * Tell this account, ONCE, that new items of this kind now stay on this device.
 *
 * Called AFTER the item was actually written, never while deciding where to
 * put it: a create that then fails would otherwise burn the only explanation
 * the user was ever going to get.
 *
 * Keyed by the account, not the device — two people sharing a computer each
 * deserve the explanation the first time it happens to them.
 */
export function announceDeviceItem(kind) {
  const device = deviceScopeId();
  if (!device) return false;
  // The notice explains a FULL cloud. A plan without cloud sync, or a Settings
  // default of "this device", puts every new item here on purpose.
  if (newItemsStayOnDevice()) return false;
  const key = `cloudLocal.deviceNoticeShown.${device}.${kind}`;
  try {
    if (localStorage.getItem(key) === '1') return false;
    localStorage.setItem(key, '1');
  } catch {
    return false; // storage unavailable: better silent than repeating
  }
  notePlanLimitDeviceItem(kind);
  return true;
}
